import React, { useCallback, useState, useEffect, memo } from "react";
import cn from "classnames";
import Skeleton from "react-loading-skeleton";

import LabelTextItem from "../LabelTextItem";
import { ToggleProps } from "./Toggle.props";

import styles from "./Toggle.module.scss";
import globalFormStyles from "./global.formElements.module.scss";

const Toggle: React.FC<ToggleProps> = (props: ToggleProps) => {
  const {
    id,
    toggleLabel, // text to the right
    isDisabled = false, // t/f
    toggleSize = "large", // "large" | "small"
    tabIndex,
    onChange,
    name,
    containerClassName,
    displayReadOnlyFieldInline,
    readOnly,
    isLoading,
    checked: checkedProp = false,
  } = props;

  const [internalChecked, setInternalChecked] = useState(checkedProp);

  // Sync internal state with prop when it changes
  useEffect(() => {
    setInternalChecked(checkedProp);
  }, [checkedProp]);

  const getDisplayText
    = props.getDisplayText
    || ((toggleValue) => {
      return toggleValue && new Array(toggleValue).filter(Boolean).join("\r\n");
    });

  const toggleContainerClass = cn("toggle-wrapper", globalFormStyles["form-field-container"], containerClassName);

  let toggleClasses = "";
  if (internalChecked) toggleClasses = styles["on"];
  if (!internalChecked) toggleClasses = styles["off"];
  if (isDisabled) toggleClasses = styles["disabled"];

  const handleToggleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (!isDisabled) {
        const newChecked = e.target.checked;
        setInternalChecked(newChecked);
        onChange && onChange(e);
      }
    },
    [isDisabled, onChange],
  );

  let component = null;

  if (readOnly) {
    // @ts-ignore
    const toggleValue = getDisplayText(internalChecked);
    component = (
      <LabelTextItem
        id={id}
        label={`${toggleLabel}: `}
        displayInline={displayReadOnlyFieldInline}
        body={toggleValue}
        isLoading={isLoading}
        className="toggle-readonly"
      />
    );
  } else if (isLoading) {
    component = <Skeleton height={20} />;
  } else {
    component = (
      <div className={styles["toggle-outer"]}>
        <div className={styles["toggle-container"]}>
          <div className={styles[toggleSize]}>
            <input
              name={name}
              tabIndex={tabIndex}
              aria-label={toggleLabel}
              type="checkbox"
              id={id ? `toggle_${name}_${id}` : undefined}
              data-testid={`test__${id}`}
              checked={!!internalChecked}
              onChange={(e): void => handleToggleChange(e)}
            />
            <div className={cn(toggleClasses, styles["slider"], styles["round"], styles[toggleSize])}></div>
          </div>
          <div className={cn(styles["toggle-text"], styles[`${toggleSize}-label`])}>
            <label
              aria-label={toggleLabel}
              htmlFor={id ? `toggle_${name}_${id}` : undefined}
              data-testid={`test__${id}-label`}
              className={styles[`${toggleSize}-label`]}
            >
              {toggleLabel}
            </label>
          </div>
        </div>
      </div>
    );
  }

  return <div className={toggleContainerClass}>{component}</div>;
};

export default memo(Toggle);
